<template>
  <qs-drawer
    title="开通网约钱包支付"
    :visible.sync="visible"
    :wrapperClosable="false"
    :modal-append-to-body="true"
    cusSize="xlg"
    class="open-wallet-drawer"
  >
    <div v-loading="detailLoading" class="drawer-content">
      <!-- Steps -->
      <div class="steps-wrapper">
        <el-steps :active="activeStep" align-center class="custom-steps">
          <el-step title="提交进件材料"></el-step>
          <el-step title="开通监管子户"></el-step>
          <el-step title="生成授权码"></el-step>
          <el-step title="确认配置"></el-step>
        </el-steps>
      </div>

      <!-- Step Content -->
      <div class="step-content">
        <!-- Step 1: 提交进件材料 -->
        <div v-if="activeStep === 0">
          <Step1Form
            ref="step1Ref"
            v-model="formModel"
            @select-company="(val) => selectedCompanyInfo = val"
          />
        </div>

        <!-- Step 2: 开通监管子户 -->
        <div v-if="activeStep === 1">
          <Step2OpenAccount
            :entity="entityInfo"
            @status-change="handleStatusChange"
          />
        </div>

        <!-- Step 3: 生成授权码 -->
        <div v-if="activeStep === 2">
          <Step3AuthCode
            :company-info="entityInfo"
            :auth-list="existAuthList"
            :auth-code="authCode"
            :auth-id="currentAuthId"
            @use-auth="handleUseAuth"
            @regenerate="(val) => handleRegenerateAuth(val)"
          />
        </div>

        <!-- Step 4: 确认配置 -->
        <div v-if="activeStep === 3">
          <Step4ConfirmConfig
            ref="step4Ref"
            :auth-info="selectedAuthInfo"
            :entity="entityInfo"
          />
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="drawer-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button
          v-if="activeStep === 0 || activeStep === 3"
          type="primary"
          @click="handleNext"
          :loading="loading"
          :disabled="activeStep === 0 && !formModel.companyName"
        >
          {{ activeStep === 3 ? '确认' : '提交' }}
        </el-button>
      </div>
    </template>
  </qs-drawer>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getSpAuthDetail, submitSpAuth, useExistSpAuth, confirmSpAuth } from '@/api/domain/finance.js'
import Step1Form from './Step1Form.vue'
import Step2OpenAccount from './Step2OpenAccount.vue'
import Step3AuthCode from './Step3AuthCode.vue'
import Step4ConfirmConfig from './Step4ConfirmConfig.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  authId: {
    type: [String, Number],
    default: '',
  },
})

const emit = defineEmits(['update:visible', 'success'])

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
})

const activeStep = ref(0)
const loading = ref(false)
const detailLoading = ref(false)
const step1Ref = ref(null)
const step4Ref = ref(null)
const selectedAuthInfo = ref(null)
const selectedCompanyInfo = ref(null)
const existAuthList = ref([])
const entityInfo = ref({})
const currentAuthId = ref('')
const authCode = ref('')

const formModel = ref({
  companyName: '',
  paymentChannel: '网商银行',
  merchantNo: '',
  authState: null,
  rejectCompanyName: '',
})

const selectedCompany = computed(() => {
  return selectedCompanyInfo.value || null
})

const handleUseAuth = async (auth) => {
  loading.value = true
  try {
    const { code } = await useExistSpAuth({
      id: currentAuthId.value,
      existId: auth.id,
    })
    if (code === 1) {
      emit('success')
      visible.value = false
      resetForm()
    }
  } finally {
    loading.value = false
  }
}

const handleRegenerateAuth = () => {
  fetchDetail()
}

const handleCancel = () => {
  visible.value = false
  resetForm()
}

const handleStatusChange = () => {
  fetchDetail()
}

const timer = ref(null)

const startPolling = () => {
  stopPolling()
  timer.value = setInterval(() => {
    fetchDetail(true)
  }, 3000)
}

const stopPolling = () => {
  if (timer.value) {
    clearInterval(timer.value)
    timer.value = null
  }
}

watch(activeStep, (val) => {
  if (val === 1) {
    startPolling()
  } else {
    stopPolling()
  }
})

watch(() => props.visible, (val) => {
  if (val) {
    resetForm()
    if (props.authId) {
      currentAuthId.value = props.authId
      fetchDetail()
    }
  } else {
    stopPolling()
    emit('success')
  }
})

const handleNext = async () => {
  if (activeStep.value === 0) {
    step1Ref.value.validate(async (valid) => {
      if (valid) {
        loading.value = true
        try {
          const params = {
            enterpriseId: selectedCompany.value.value,
            merchantNo: formModel.value.merchantNo,
          }
          if (currentAuthId.value) {
            params.id = currentAuthId.value
          }
          const { code, data } = await submitSpAuth(params)
          if (code === 1) {
            currentAuthId.value = data
            activeStep.value++
          }
        } finally {
          loading.value = false
        }
      }
    })
  } else {
    step4Ref.value.validate(async (valid, data) => {
      if (valid) {
        loading.value = true
        try {
          const { code } = await confirmSpAuth({
            id: currentAuthId.value || props.authId,
            bankCardNo: data.bankCardNumber,
          })
          if (code === 1) {
            emit('success')
            visible.value = false
            resetForm()
          }
        } finally {
          loading.value = false
        }
      }
    })
  }
}

const resetForm = () => {
  activeStep.value = 0
  formModel.value = {
    companyName: '',
    paymentChannel: '网商银行',
    merchantNo: '',
    authState: null,
    rejectCompanyName: '',
    failType: null,
    failReason: '',
  }
  selectedAuthInfo.value = null
  existAuthList.value = []
  entityInfo.value = {}
  if (step1Ref.value) {
    step1Ref.value.resetFields() // Reset step 1 validation
  }
}

const mapStateToStep = (state) => {
  switch (Number(state)) {
    case -1: // 材料已驳回 -> 回到第一步修改
      return 0
    case 1: // 子户开通中 -> 第二步
      return 1
    case 2: // 待网约平台配置 -> 第三步
    case 3: // 待乾三配置 -> 第三步
      return 2
    case 4: // 授权配置待确认 -> 第四步
    case 5: // 授权待生效 -> 第四步(或完成)
    case 6: // 授权生效中 -> 第四步(或完成)
    case -2: // 授权已解约 -> 第四步(或完成)
      return 3
    default:
      return 0
  }
}

const fetchDetail = async (silent = false) => {
  const id = currentAuthId.value || props.authId
  if (!id) return
  if (!silent) detailLoading.value = true
  try {
    const { code, data } = await getSpAuthDetail({ id })
    if (code === 1) {
      // 设置当前步骤
      activeStep.value = mapStateToStep(data.authState)
      currentAuthId.value = data.id

      formModel.value = {
        companyName: data?.entity?.id || '',
        paymentChannel: '网商银行',
        merchantNo: data.merchantNo || '',
        authState: data.authState,
        rejectCompanyName: data?.entity?.corporationName,
        failType: data.failType,
        failReason: data.failReason || '',
      }

      entityInfo.value = {
        ...data.entity,
        merchantNo: data.merchantNo,
        files: data.files,
      }

      // 填充 Step 3/4 需要的 authInfo
      selectedAuthInfo.value = {
        id: data.id,
        spName: data.spName,
        validPeriod: `${data.effectDate}～${data.endDate}`,
        scope: Array.isArray(data.authRentCategoryName) ? data.authRentCategoryName.join('、') : data.authRentCategoryName,
        extendDay: data.extendDay,
        payPeriodStr: data.payPeriodStr,
      }
      authCode.value = data.authCode || ''

      existAuthList.value = (data.existAuthList || []).map((item) => ({
        id: item.id,
        spName: item.spName,
        effectDate: item.effectDate,
        endDate: item.endDate,
        authRentCategoryName: item.authRentCategoryName,
        validPeriod: `${item.effectDate}～${item.endDate}`,
        scope: Array.isArray(item.authRentCategoryName) ? item.authRentCategoryName.join('、') : item.authRentCategoryName,
        extendDay: item.extendDay,
        payPeriodStr: item.payPeriodStr,
      }))
    }
  } finally {
    if (!silent) detailLoading.value = false
  }
}

</script>

<style scoped lang="scss">
.open-wallet-drawer {
  ::v-deep .el-drawer__body {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.steps-wrapper {
  margin-bottom: 30px;
  padding: 0 20px;

  ::v-deep .el-step__line {
    background-color: #E4E7ED;
  }

  ::v-deep .el-steps .el-step .el-step__head.is-finish .el-step__line .el-step__line-inner {
    border-width: 1px !important;
    width: 100% !important;
  }

  ::v-deep .el-steps .el-step .el-step__head.is-success .el-step__line .el-step__line-inner {
    border-width: 1px !important;
    width: 100% !important;
  }
}

.step-content {
  padding: 0 10px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  background-color: #F5F7FA;
  padding: 10px;

  .blue-bar {
    width: 4px;
    height: 16px;
    background-color: #409EFF;
    margin-right: 10px;
    border-radius: 2px;
  }

  .section-title {
    font-size: 14px;
    font-weight: bold;
    color: #303133;
  }
}

.step-form {
  ::v-deep .el-form-item .el-form-item__label {
    justify-content: flex-start;
  }
  .el-form-item {
    margin-bottom: 24px;
  }

  .form-helper-text {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.5;

    .link-text {
      color: #409EFF;
      cursor: pointer;
      margin-left: 5px;
    }
  }

  .readonly-text {
    color: #606266;
    font-size: 14px;
  }
}

.drawer-footer {
  text-align: right;
  background-color: #fff;
  width: 100%;
  box-sizing: border-box;
}

.placeholder-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 0;
    color: #909399;

    i {
        font-size: 48px;
        margin-bottom: 10px;
    }
}
</style>
